import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Chip, Typography } from '@heroui/react';
import { DataGrid, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
    type DataGridSkeletonRow,
} from '@/components/dataGridSkeleton';
import { getNodeTestIdProp, useIsNarrowViewport } from '@/utils';
import {
    PRODUCT_COLUMN,
    PRODUCT_SKU_COLUMN_WIDTH,
    PRODUCT_TABLE_BOTTOM_GAP_PX,
    PRODUCT_TABLE_MIN_HEIGHT_PX,
    PRODUCT_TYPE_CHIP,
    formatAmountRange,
    getProductColumnPinning,
    type Product,
    type ProductColumnId,
} from '../productsMeta';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';
import { toProductDetailPath } from '../productDetail/productDetailAddress';
import type { ProductOriginState } from '../productOrigin';
import { CostColumnHeader } from './CostColumnHeader';
import { ProductRowStateChip } from './ProductRowStateChip';
import { ProductTableEmptyState } from './ProductTableEmptyState';
import { ProductTableFooter } from './ProductTableFooter';
import { useFillViewportHeight } from './useFillViewportHeight';

type ProductTableProps = {
    /** The current page; `null` until the first page arrives. */
    data: Product[] | null;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    pageWindow: (number | 'ellipsis')[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    /** A search or a filter is applied: an empty page means "nothing matched". */
    isFiltered: boolean;
    onClearFilters: () => void;
    /** Whose stock the figures count; the Cost header's tooltip names it. */
    stockFigures: StockFiguresScope;
    /**
     * The branch the list asks for, which each product's link carries to its
     * details page so the two count the same stock (bos-app#119); `null`
     * for the caller's own scope.
     */
    branchId: string | null;
    /**
     * What each product's link records in router state: this entry as the
     * product's origin, where a Delete returns to (bos-app#137).
     */
    productLinkState: ProductOriginState | undefined;
};

const EMPTY_VALUE = '—';

/** Placeholder rows while the first page loads. */
const SKELETON_ROWS = buildDataGridSkeletonRows(8, 'product-table');

/**
 * Every row is 52px. The cell's own vertical padding comes off so a two-line
 * cell (a quantity with its expired count under it) fits the row instead of
 * stretching it; single-line cells centre in the same height.
 */
const CELL_CLASS = 'h-13 py-0';

/**
 * The grid's layout, loaded or not; the comment in the render explains it.
 * `product-data-grid` is the sticky header's hook in `src/index.css`.
 */
const GRID_CLASS =
    'product-data-grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_auto]';
const SCROLL_CONTAINER_CLASS = 'flex min-h-0 flex-col';

/**
 * Render-only products grid. The data is **server-paginated** — `data` is
 * already the current page in the server's order, so there's no client-side
 * sort or slice here; the footer just drives the page / page-size controls.
 *
 * The widget fills the page down to the app shell's bottom padding and the
 * rows scroll inside it, under a sticky header, with Name (and SKU from `sm`
 * up) pinned while the rest scroll sideways (BOS-193). Every row on a page is
 * rendered — at most 200 — rather than virtualized: DataGrid's virtualization
 * drops pinned columns (spike on bos-app#110). The sticky header it would have
 * supplied comes from `.product-data-grid` in `src/index.css`.
 *
 * Until the first page arrives (`data` is `null`) the same grid shows its
 * real headers, pinned as loaded, over placeholder rows with one grey bar per
 * column (rulebook §12), so nothing moves when the rows land. It has no footer
 * yet: the page count isn't known.
 */
export const ProductTable: React.FC<ProductTableProps> = ({
    data,
    page,
    pageSize,
    totalCount,
    totalPages,
    pageWindow,
    onPageChange,
    onPageSizeChange,
    isFiltered,
    onClearFilters,
    stockFigures,
    branchId,
    productLinkState,
}) => {
    const { t } = useTranslation('pages');
    const isNarrow = useIsNarrowViewport();
    const fill = useFillViewportHeight<HTMLDivElement>({
        bottomGapPx: PRODUCT_TABLE_BOTTOM_GAP_PX,
        minHeightPx: PRODUCT_TABLE_MIN_HEIGHT_PX,
    });
    const pinning = React.useMemo(
        () => getProductColumnPinning(isNarrow),
        [isNarrow],
    );

    const copy = React.useMemo(
        () => ({
            tableAria: t('products.components.productTable.tableAria'),
            types: {
                Simple: t(PRODUCT_TYPE_CHIP.Simple.labelKey),
                Variable: t(PRODUCT_TYPE_CHIP.Variable.labelKey),
            },
            columns: {
                name: t('products.components.productTable.columns.name'),
                sku: t('products.components.productTable.columns.sku'),
                type: t('products.components.productTable.columns.type'),
                category: t(
                    'products.components.productTable.columns.category',
                ),
                brand: t('products.components.productTable.columns.brand'),
                variants: t(
                    'products.components.productTable.columns.variants',
                ),
                quantity: t(
                    'products.components.productTable.columns.quantity',
                ),
                price: t('products.components.productTable.columns.price'),
                stock: t('products.components.productTable.columns.stock'),
            },
            stock: {
                low: t('products.components.productTable.stock.low'),
                ok: t('products.components.productTable.stock.ok'),
            },
            // A count, so it interpolates per row rather than resolving once.
            expired: (count: number) =>
                t('products.components.productTable.expired', { count }),
            archivedChip: t('products.components.productTable.archivedChip'),
            draftChip: t('products.components.productTable.draftChip'),
        }),
        [t],
    );

    const columns = React.useMemo<DataGridColumn<Product>[]>(() => {
        const definitions: DataGridColumn<Product>[] = [
            {
                id: PRODUCT_COLUMN.name,
                header: copy.columns.name,
                isRowHeader: true,
                width: pinning.nameWidth,
                // One line, cut short, so the row keeps its 52px; the archived
                // and draft chips go under the name rather than beside it,
                // where they would leave a pinned column no room for the name
                // at all. Both share that one line.
                cell: (item) => (
                    <div className="flex min-w-0 flex-col items-start justify-center gap-0.5">
                        <Link
                            className="block max-w-full truncate text-sm font-medium text-foreground hover:text-primary hover:underline"
                            state={productLinkState}
                            title={item.name}
                            to={toProductDetailPath(item.id, branchId)}
                            {...getNodeTestIdProp(
                                `product-row-name-${item.id}`,
                            )}
                        >
                            {item.name}
                        </Link>
                        {item.archived || item.draft ? (
                            <div className="flex max-w-full min-w-0 gap-1">
                                {item.archived ? (
                                    <ProductRowStateChip
                                        label={copy.archivedChip}
                                        productId={item.id}
                                        state="archived"
                                    />
                                ) : null}
                                {item.draft ? (
                                    <ProductRowStateChip
                                        label={copy.draftChip}
                                        productId={item.id}
                                        state="draft"
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ),
            },
            {
                id: PRODUCT_COLUMN.sku,
                header: copy.columns.sku,
                width: PRODUCT_SKU_COLUMN_WIDTH,
                cell: (item) => (
                    <span
                        className="text-sm text-muted"
                        {...getNodeTestIdProp(`product-row-sku-${item.id}`)}
                    >
                        {item.sku ?? EMPTY_VALUE}
                    </span>
                ),
            },
            {
                id: PRODUCT_COLUMN.type,
                header: copy.columns.type,
                width: 120,
                cell: (item) => (
                    <Chip
                        color={PRODUCT_TYPE_CHIP[item.type].color}
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp(`product-row-type-${item.id}`)}
                    >
                        <Chip.Label>{copy.types[item.type]}</Chip.Label>
                    </Chip>
                ),
            },
            {
                id: PRODUCT_COLUMN.category,
                header: copy.columns.category,
                minWidth: 140,
                // Two lines at most, so a long name never stretches the row.
                cell: (item) => (
                    <span
                        className="line-clamp-2 text-sm text-foreground"
                        {...getNodeTestIdProp(
                            `product-row-category-${item.id}`,
                        )}
                    >
                        {item.category?.name ?? EMPTY_VALUE}
                    </span>
                ),
            },
            {
                id: PRODUCT_COLUMN.brand,
                header: copy.columns.brand,
                minWidth: 120,
                cell: (item) => (
                    <span
                        className="line-clamp-2 text-sm text-foreground"
                        {...getNodeTestIdProp(`product-row-brand-${item.id}`)}
                    >
                        {item.brand?.name ?? EMPTY_VALUE}
                    </span>
                ),
            },
            {
                id: PRODUCT_COLUMN.variants,
                header: copy.columns.variants,
                width: 96,
                align: 'end',
                cell: (item) => (
                    <span
                        className="text-sm tabular-nums text-foreground"
                        {...getNodeTestIdProp(
                            `product-row-variants-${item.id}`,
                        )}
                    >
                        {item.variantCount}
                    </span>
                ),
            },
            {
                id: PRODUCT_COLUMN.quantity,
                header: copy.columns.quantity,
                width: 132,
                align: 'end',
                // bos-api BOS-191: the quantity counts what is on the shelf,
                // expired lots included. A row that is half past its sell-by
                // date reads as healthy stock unless the figure says so, so the
                // expired part is named right under the number it inflates —
                // and only when there is some, to keep the column quiet.
                cell: (item) => (
                    <div className="flex flex-col items-end gap-0.5">
                        <span
                            className="text-sm tabular-nums text-foreground"
                            {...getNodeTestIdProp(
                                `product-row-quantity-${item.id}`,
                            )}
                        >
                            {item.totalQuantity}
                        </span>
                        {item.expiredQuantity > 0 ? (
                            <span
                                className="text-xs tabular-nums text-warning-soft-foreground"
                                {...getNodeTestIdProp(
                                    `product-row-expired-${item.id}`,
                                )}
                            >
                                {copy.expired(item.expiredQuantity)}
                            </span>
                        ) : null}
                    </div>
                ),
            },
            {
                id: PRODUCT_COLUMN.cost,
                header: <CostColumnHeader stockFigures={stockFigures} />,
                minWidth: 130,
                align: 'end',
                // bos-api BOS-171: the purchase price of the next batch to
                // sell, per variant. A product with no stock has no cost,
                // and "—" says so where ₾0.00 would call it free.
                // `tabular-nums` lines the digits up down the column.
                cell: (item) => (
                    <Typography
                        className="tabular-nums"
                        type="body-sm"
                        {...getNodeTestIdProp(`product-row-cost-${item.id}`)}
                    >
                        {item.costRange
                            ? formatAmountRange(item.costRange)
                            : EMPTY_VALUE}
                    </Typography>
                ),
            },
            {
                id: PRODUCT_COLUMN.price,
                header: copy.columns.price,
                minWidth: 130,
                align: 'end',
                cell: (item) => (
                    <span
                        className="text-sm tabular-nums text-foreground"
                        {...getNodeTestIdProp(`product-row-price-${item.id}`)}
                    >
                        {formatAmountRange(item.priceRange)}
                    </span>
                ),
            },
            {
                id: PRODUCT_COLUMN.stock,
                header: copy.columns.stock,
                width: 120,
                cell: (item) => (
                    <Chip
                        color={item.lowStock ? 'warning' : 'success'}
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp(`product-row-stock-${item.id}`)}
                    >
                        <Chip.Label>
                            {item.lowStock ? copy.stock.low : copy.stock.ok}
                        </Chip.Label>
                    </Chip>
                ),
            },
        ];

        return definitions.map((column) => {
            const isPinned = pinning.pinned.has(column.id as ProductColumnId);
            return {
                ...column,
                // Widths only, never a drag handle; see `allowsColumnResize`.
                allowsResizing: false,
                pinned: isPinned ? 'start' : undefined,
                cellClassName: CELL_CLASS,
            };
        });
    }, [branchId, copy, pinning, productLinkState, stockFigures]);

    const skeletonColumns = React.useMemo(
        () => buildDataGridSkeletonColumns(columns),
        [columns],
    );

    return (
        <Widget
            className="flex flex-col gap-0 overflow-hidden p-0"
            ref={fill.ref}
            style={
                fill.height === undefined ? undefined : { height: fill.height }
            }
            {...getNodeTestIdProp('product-table-widget')}
        >
            {/* `allowsColumnResize` is here for the widths, not for resizing
                (every column opts out): outside virtualized mode it is the
                only way DataGrid applies `width` / `minWidth` — it wraps the
                table in react-aria's resizable container, which lays the table
                out `fixed` at those widths. Without it the columns size to
                their content, and a long name pushes SKU out from under its
                pinned offset.

                That container is also what scrolls, both ways, so the sticky
                header and the pinned columns hold against it. The grid fills
                the widget; its root is a CSS grid whose first row, the scroll
                area, takes what the footer row leaves (`minmax(0, 1fr) auto`),
                and the scroll container is a flex column so the resizable
                container shrinks to fit it. The footer is DataGrid's own
                `footer` slot (BOS-197), left out when nothing matched.
                `product-data-grid` is the sticky header's hook in index.css.
                The placeholder grid is laid out the same way. */}
            {data === null ? (
                <DataGrid<DataGridSkeletonRow>
                    allowsColumnResize
                    aria-label={copy.tableAria}
                    className={GRID_CLASS}
                    columns={skeletonColumns}
                    data={SKELETON_ROWS}
                    getRowId={(row) => row.id}
                    scrollContainerClassName={SCROLL_CONTAINER_CLASS}
                    variant="primary"
                />
            ) : (
                <DataGrid<Product>
                    allowsColumnResize
                    aria-label={copy.tableAria}
                    className={GRID_CLASS}
                    columns={columns}
                    data={data}
                    footer={
                        totalCount > 0 ? (
                            <ProductTableFooter
                                onPageChange={onPageChange}
                                onPageSizeChange={onPageSizeChange}
                                page={page}
                                pageSize={pageSize}
                                pageWindow={pageWindow}
                                totalCount={totalCount}
                                totalPages={totalPages}
                            />
                        ) : undefined
                    }
                    getRowId={(item) => item.id}
                    renderEmptyState={() => (
                        <ProductTableEmptyState
                            isFiltered={isFiltered}
                            onClearFilters={onClearFilters}
                        />
                    )}
                    scrollContainerClassName={SCROLL_CONTAINER_CLASS}
                    variant="primary"
                    {...getNodeTestIdProp('product-table-grid')}
                />
            )}
        </Widget>
    );
};
